import { useRef, useEffect, useCallback, useState } from "react";
import type { MarketWithHistory } from "../types";

interface Props {
  markets: MarketWithHistory[];
  asOf?: number; // unix timestamp in seconds; defaults to latest
}

const PALETTE = [
  "#ff6b6b", "#ff9f43", "#feca57", "#48dbfb", "#0abde3",
  "#5f27cd", "#ee5a24", "#6ab04c", "#e056fd", "#22a6b3",
  "#f9ca24", "#eb4d4b", "#7ed6df", "#badc58", "#f0932b",
  "#6c5ce7", "#fdcb6e", "#e17055", "#00cec9", "#a29bfe",
];

const ONE_DAY = 1000 * 60 * 60 * 24;

interface Interval {
  startDate: Date;
  endDate: Date;
  deltaP: number;
  cumP: number;
  deltaDays: number;
  density: number;
  index: number;
}

interface Bar {
  x: number;
  w: number;
  h: number;
  y: number;
  color: string;
  interval: Interval;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function priceAt(history: MarketWithHistory["history"], asOf?: number): number {
  if (history.length === 0) return 0;
  if (asOf == null) return history[history.length - 1]!.p;
  // Binary search for last point with t <= asOf
  let lo = 0, hi = history.length - 1;
  if (history[0]!.t > asOf) return 0;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (history[mid]!.t <= asOf) lo = mid;
    else hi = mid - 1;
  }
  return history[lo]!.p;
}

function computeIntervals(markets: MarketWithHistory[], asOf?: number): Interval[] {
  // Filter to markets that existed at asOf (had history by then)
  const items = markets
    .map((m, i) => ({
      endDate: new Date(m.info.endDate),
      latestPrice: priceAt(m.history, asOf),
      index: i,
      existed: asOf == null || (m.history.length > 0 && m.history[0]!.t <= asOf),
    }))
    .filter((item) => item.existed);

  if (items.length < 1) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const intervals: Interval[] = [];

  for (let i = 0; i < items.length; i++) {
    const endDate = items[i]!.endDate;
    const startDate = i === 0
      ? new Date(Math.min(today.getTime(), endDate.getTime() - ONE_DAY))
      : items[i - 1]!.endDate;
    const deltaP = i === 0
      ? items[i]!.latestPrice
      : items[i]!.latestPrice - items[i - 1]!.latestPrice;
    const deltaDays = Math.max((endDate.getTime() - startDate.getTime()) / ONE_DAY, 1);
    const density = Math.max(deltaP, 0) / deltaDays;

    intervals.push({
      startDate,
      endDate,
      deltaP: Math.max(deltaP, 0),
      cumP: items[i]!.latestPrice,
      deltaDays,
      density,
      index: items[i]!.index,
    });
  }

  return intervals;
}

function useBarChart(
  markets: MarketWithHistory[],
  intervals: Interval[],
  getValue: (iv: Interval) => number,
  maxValue: number,
  yLabel: (frac: number) => string,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<Bar[]>([]);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; bar: Bar } | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || intervals.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const margin = { top: 20, right: 20, bottom: 40, left: 55 };
    const plotW = W - margin.left - margin.right;
    const plotH = H - margin.top - margin.bottom;

    const tStart = intervals[0]!.startDate.getTime();
    const tEnd = intervals[intervals.length - 1]!.endDate.getTime();
    const tRange = tEnd - tStart || 1;

    const bars: Bar[] = [];

    for (const iv of intervals) {
      const x0 = margin.left + ((iv.startDate.getTime() - tStart) / tRange) * plotW;
      const x1 = margin.left + ((iv.endDate.getTime() - tStart) / tRange) * plotW;
      const w = Math.max(x1 - x0, 1);
      const val = getValue(iv);
      const barH = (val / maxValue) * plotH;
      const color = PALETTE[iv.index % PALETTE.length]!;

      bars.push({
        x: x0,
        w,
        h: barH,
        y: margin.top + plotH - barH,
        color,
        interval: iv,
      });
    }

    barsRef.current = bars;

    for (const bar of bars) {
      ctx.fillStyle = bar.color + "60";
      ctx.fillRect(bar.x, bar.y, bar.w, bar.h);
      ctx.strokeStyle = bar.color;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bar.x, bar.y, bar.w, bar.h);
    }

    // X-axis
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top + plotH);
    ctx.lineTo(margin.left + plotW, margin.top + plotH);
    ctx.stroke();

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + plotH);
    ctx.stroke();

    // Y-axis labels
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "10px monospace";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    const yTicks = 4;
    for (let i = 0; i <= yTicks; i++) {
      const frac = i / yTicks;
      const y = margin.top + plotH - frac * plotH;
      ctx.fillText(yLabel(frac), margin.left - 6, y);
      if (i > 0) {
        ctx.strokeStyle = "rgba(255,255,255,0.06)";
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + plotW, y);
        ctx.stroke();
      }
    }

    // X-axis labels (skip overlapping)
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    let lastRight = -Infinity;
    for (const iv of intervals) {
      const x = margin.left + ((iv.endDate.getTime() - tStart) / tRange) * plotW;
      const label = iv.endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const w = ctx.measureText(label).width;
      if (x - w / 2 > lastRight + 4) {
        ctx.fillText(label, x, margin.top + plotH + 6);
        lastRight = x + w / 2;
      }
    }
  }, [markets, intervals, getValue, maxValue, yLabel]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(container);
    draw();
    return () => observer.disconnect();
  }, [draw]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      let hit: Bar | null = null;
      for (const bar of barsRef.current) {
        if (mx >= bar.x && mx <= bar.x + bar.w && my >= bar.y && my <= bar.y + bar.h) {
          hit = bar;
          break;
        }
      }
      setTooltip(hit ? { x: mx, y: hit.y, bar: hit } : null);
    },
    []
  );

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return { canvasRef, containerRef, tooltip, handleMouseMove, handleMouseLeave };
}

export function DensityChart({ markets, asOf }: Props) {
  const intervals = computeIntervals(markets, asOf);
  const maxDensity = Math.max(...intervals.map((d) => d.density), 0.001);

  const getValue = useCallback((iv: Interval) => iv.density, []);
  const yLabel = useCallback((frac: number) => `${(frac * maxDensity * 100).toFixed(1)}%/d`, [maxDensity]);

  const { canvasRef, containerRef, tooltip, handleMouseMove, handleMouseLeave } =
    useBarChart(markets, intervals, getValue, maxDensity, yLabel);

  return (
    <div ref={containerRef} className="density-container">
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
      {tooltip && (
        <div className="chart-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 44 }}>
          <div className="tooltip-market" style={{ color: tooltip.bar.color }}>
            {formatDate(tooltip.bar.interval.startDate)} – {formatDate(tooltip.bar.interval.endDate)}
          </div>
          <div>
            Prob: <strong>{(tooltip.bar.interval.deltaP * 100).toFixed(1)}%</strong> &middot; {Math.round(tooltip.bar.interval.deltaDays)} days
          </div>
        </div>
      )}
    </div>
  );
}

export function CumulativeChart({ markets, asOf }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pointsRef = useRef<Array<{ sx: number; sy: number; cumP: number; endDate: Date; color: string }>>([]);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    cumP: number;
    endDate: Date;
    color: string;
  } | null>(null);

  const intervals = computeIntervals(markets, asOf);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || intervals.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const margin = { top: 20, right: 20, bottom: 40, left: 55 };
    const plotW = W - margin.left - margin.right;
    const plotH = H - margin.top - margin.bottom;

    const tStart = intervals[0]!.startDate.getTime();
    const tEnd = intervals[intervals.length - 1]!.endDate.getTime();
    const tRange = tEnd - tStart || 1;

    // Build points at each end date
    const points: Array<{ sx: number; sy: number; cumP: number; endDate: Date; color: string }> = [];
    for (const iv of intervals) {
      const sx = margin.left + ((iv.endDate.getTime() - tStart) / tRange) * plotW;
      const sy = margin.top + plotH - iv.cumP * plotH;
      points.push({ sx, sy, cumP: iv.cumP, endDate: iv.endDate, color: PALETTE[iv.index % PALETTE.length]! });
    }
    pointsRef.current = points;

    // X-axis
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top + plotH);
    ctx.lineTo(margin.left + plotW, margin.top + plotH);
    ctx.stroke();

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + plotH);
    ctx.stroke();

    // Y-axis labels
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "10px monospace";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let i = 0; i <= 4; i++) {
      const frac = i / 4;
      const y = margin.top + plotH - frac * plotH;
      ctx.fillText(`${Math.round(frac * 100)}%`, margin.left - 6, y);
      if (i > 0) {
        ctx.strokeStyle = "rgba(255,255,255,0.06)";
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + plotW, y);
        ctx.stroke();
      }
    }

    // X-axis labels (skip overlapping)
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    let lastRight = -Infinity;
    for (const pt of points) {
      const label = pt.endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const w = ctx.measureText(label).width;
      if (pt.sx - w / 2 > lastRight + 4) {
        ctx.fillText(label, pt.sx, margin.top + plotH + 6);
        lastRight = pt.sx + w / 2;
      }
    }

    // Fill area under the line
    ctx.beginPath();
    ctx.moveTo(points[0]!.sx, margin.top + plotH);
    for (const pt of points) ctx.lineTo(pt.sx, pt.sy);
    ctx.lineTo(points[points.length - 1]!.sx, margin.top + plotH);
    ctx.closePath();
    ctx.fillStyle = "rgba(72, 219, 251, 0.08)";
    ctx.fill();

    // Draw line
    ctx.beginPath();
    ctx.moveTo(points[0]!.sx, points[0]!.sy);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i]!.sx, points[i]!.sy);
    ctx.strokeStyle = "#48dbfb";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw dots at each point with market color
    for (const pt of points) {
      ctx.beginPath();
      ctx.arc(pt.sx, pt.sy, 4, 0, Math.PI * 2);
      ctx.fillStyle = pt.color;
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }, [intervals]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(container);
    draw();
    return () => observer.disconnect();
  }, [draw]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      let bestDist = 20;
      let best: typeof tooltip = null;
      for (const pt of pointsRef.current) {
        const dist = Math.hypot(pt.sx - mx, pt.sy - my);
        if (dist < bestDist) {
          bestDist = dist;
          best = { x: pt.sx, y: pt.sy, cumP: pt.cumP, endDate: pt.endDate, color: pt.color };
        }
      }
      setTooltip(best);
    },
    []
  );

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return (
    <div ref={containerRef} className="density-container">
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
      {tooltip && (
        <div className="chart-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 44 }}>
          <div className="tooltip-market" style={{ color: tooltip.color }}>
            {formatDate(tooltip.endDate)}
          </div>
          <div>
            Cumulative: <strong>{(tooltip.cumP * 100).toFixed(1)}%</strong>
          </div>
        </div>
      )}
    </div>
  );
}
